import { useState, useEffect } from 'react'
import axiosClient from '../../services/axiosClient'
import {
  Box,
  Paper,
  Alert
} from '@mui/material'
import PrimaryButton from '../PrimaryButton'
import EditAvailability from '../EditAvailability'


const DEFAULT_SCHEDULE: WeeklySchedule = {
  'SUN': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
  'MON': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
  'TUE': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
  'WED': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
  'THU': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
  'FRI': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
  'SAT': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] }
}

interface TimeRange {
  startTime: string  // stored in 24h format
  endTime: string    // stored in 24h format
  hasConflict?: boolean  // new field to track conflicts
}

interface DaySchedule {
  enabled: boolean
  timeRanges: TimeRange[]
}

interface WeeklySchedule {
  [key: string]: DaySchedule
}

interface Props {
  existingSchedule?: WeeklySchedule
  onClose?: () => void
  onSuccess?: () => void;
  prospectId?: string;
}


export default function ProspectTourAvailability({ onClose, onSuccess, prospectId }: Props) {
  const [schedule, setSchedule] = useState<WeeklySchedule>(DEFAULT_SCHEDULE)
  const [timezone, setTimezone] = useState('America/New_York')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const response = await axiosClient.get(`/renters/${prospectId}/tour_availability`)
        const { schedule: existingSchedule, timezone: existingTimezone } = response.data

        // Replace entirely with backend data (no merging)
        setSchedule(existingSchedule)
        setTimezone(existingTimezone)
      } catch (err) {
        console.error('Error fetching availability:', err)
        setError('Failed to load availability schedule')
        // Fallback to default schedule
        setSchedule(DEFAULT_SCHEDULE)
      } finally {
        setLoading(false)
      }
    }

    fetchAvailability()
  }, [prospectId])

  const handleChange = (newSchedule: WeeklySchedule, newTimezone: string) => {
    setSchedule(newSchedule)
    setTimezone(newTimezone)
  }

  const validateSchedule = () => {
    setError(null)

    // Check if at least one day is enabled
    const hasEnabledDay = Object.values(schedule).some(day => day.enabled)
    if (!hasEnabledDay) {
      setError('Please enable at least one day')
      return false
    }

    // Validate time ranges for each enabled day
    for (const [dayId, day] of Object.entries(schedule)) {
      if (!day.enabled) continue

      for (const range of day.timeRanges) {
        if (!range.startTime || !range.endTime) {
          setError(`Please set both start and end times for ${dayId}`)
          return false
        }

        if (range.startTime >= range.endTime) {
          setError(`End time must be after start time for ${dayId}`)
          return false
        }
      }
    }

    return true
  }

  const handleSubmit = async () => {
    if (!validateSchedule()) return

    // Clean up the schedule by removing time ranges for disabled days
    const cleanedSchedule = Object.entries(schedule).reduce((acc, [dayId, daySchedule]) => {
      if (daySchedule.enabled) {
        acc[dayId] = daySchedule
      } else {
        acc[dayId] = { enabled: false, timeRanges: [] }
      }
      return acc
    }, {} as WeeklySchedule)

    const formData = {
      schedule: cleanedSchedule,
      timezone: timezone
    }

    try {
      setProcessing(true)
      await axiosClient.post(`/renters/${prospectId}/tour_availability`, {
        tour_availability: formData
      })
      // Refresh tour availability
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      // Close the overlay after successful submission
      onClose?.()
    } catch (error) {
      // Handle error (you might want to show an error message)
      console.error('Error saving availability:', error)
      setError('Failed to save availability schedule')
    } finally {
      setProcessing(false)
    }
  }

  return (
    <Box width="95%" sx={{ p: {xs: 0, sm: 2} , margin: 0}}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Scrollable content */}
        <Box sx={{ overflowY: 'auto', flex: 1, pr: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <EditAvailability
            schedule={schedule}
            timezone={timezone}
            onChange={handleChange}
            loading={loading}
            disabled={processing}
          />
        </Box>
        <Box sx={{
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'white',
              zIndex: 10,
              display: 'flex',
              justifyContent: 'center',
              px: 2,
              py: 1.5,
            }}>
          <PrimaryButton
              variant="contained"
              size="large"
              disabled={loading || processing}
              onClick={handleSubmit}
              sx={{
                width: '100%',
                maxWidth: '100%',
                borderRadius: '999px',
                backgroundColor: 'black',
                py: 1.5,
                boxShadow: 'none',
              }}>
            {processing ? 'Saving...' : 'Save Availability'}
          </PrimaryButton>
        </Box>
      </Paper>
    </Box>
  )
}
